type CustomIconProps = {
  className?: string
}

type CustomIconPropsInternal = CustomIconProps & {
  width: string
  height: string
}

const makeCustomIcon = (
  IconComponent: React.FC<CustomIconPropsInternal>
): React.FC<CustomIconProps> => {
  return (props: CustomIconProps) => <IconComponent width="1em" height="1em" {...props} />
}

/**
 * Custom, GovWell-defined icons
 *
 * To add a new custom icon:
 * - Add a new entry with the GW* prefix that maps to a call to makeCustomIcon.
 * - Set fill and/or stroke to "currentColor" as needed in order for the SVG to display with the text color of its parent. SVGs should be colored by their parent element's CSS `color` property.
 * - Spread the props to the svg element (important to set width, height, className). Make sure to REMOVE any custom width/height props.
 * - Please keep these entries alphabetized.
 */
const CustomIcons = {
  GWAppleLogo: makeCustomIcon((props) => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M16.21 7.03C17.18 8.19 17.69 9.56 17.72 11.16C17.76 13.13 17.09 14.78 15.74 16.12C14.92 16.94 14.01 17.35 13 17.35C12.51 17.35 11.93 17.19 11.24 16.87C10.56 16.54 10.11 16.38 9.9 16.38C9.69 16.38 9.22 16.54 8.49 16.87C7.78 17.19 7.17 17.35 6.66 17.35C5.62 17.35 4.72 16.95 3.96 16.13C2.61 14.75 1.94 13.03 1.94 10.98C1.94 9.2 2.5 7.78 3.63 6.73C4.39 6.02 5.26 5.66 6.25 5.66C6.87 5.66 7.58 5.83 8.37 6.16C9.15 6.5 9.63 6.66 9.81 6.66C9.99 6.66 10.5 6.48 11.36 6.13C12.19 5.8 12.93 5.64 13.59 5.64C14.72 5.66 15.6 6.07 16.21 7.03Z"
        fill="currentColor"
      />
      <path
        d="M14.26 2.34C14.88 1.57 15.72 1.08 16.76 0.86C16.83 1.99 16.46 3 15.65 3.9C14.85 4.79 13.95 5.24 12.94 5.24C12.86 4.09 13.29 3.08 14.26 2.34Z"
        fill="currentColor"
      />
    </svg>
  )),
  GWArea: makeCustomIcon((props) => (
    <svg viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <g clipPath="url(#clip0_7080_102993)">
        <ellipse cx="73" cy="440" rx="73" ry="72" fill="currentColor" />
        <circle cx="440" cy="72" r="72" fill="currentColor" />
        <circle cx="440" cy="440" r="72" fill="currentColor" />
        <circle cx="296" cy="250" r="72" fill="currentColor" />
        <circle cx="72" cy="72" r="72" fill="currentColor" />
        <line
          x1="296.249"
          y1="250.02"
          x2="443.249"
          y2="66.0197"
          stroke="currentColor"
          strokeWidth="48"
        />
        <line x1="442.8" y1="445.4" x2="295.8" y2="249.4" stroke="currentColor" strokeWidth="48" />
        <line x1="73" y1="475" x2="73" y2="25" stroke="currentColor" strokeWidth="48" />
        <line x1="71" y1="72" x2="440" y2="72" stroke="currentColor" strokeWidth="48" />
        <line x1="71" y1="440" x2="440" y2="440" stroke="currentColor" strokeWidth="48" />
      </g>
    </svg>
  )),
  GWComment: makeCustomIcon((props) => (
    <svg viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <g clipPath="url(#clip0_7080_102960)">
        <mask id="path-1-inside-1_7080_102960" fill="white">
          <path d="M256 0C397.385 0 512 114.615 512 256C512 397.385 397.385 512 256 512H0V256C0 114.615 114.615 0 256 0Z" />
        </mask>
        <path
          d="M0 512H-48V560H0V512ZM256 0V48C370.875 48 464 141.125 464 256H512H560C560 88.1054 423.895 -48 256 -48V0ZM512 256H464C464 370.875 370.875 464 256 464V512V560C423.895 560 560 423.895 560 256H512ZM256 512V464H0V512V560H256V512ZM0 512H48V256H0H-48V512H0ZM0 256H48C48 141.125 141.125 48 256 48V0V-48C88.1054 -48 -48 88.1054 -48 256H0Z"
          fill="currentColor"
          mask="url(#path-1-inside-1_7080_102960)"
        />
      </g>
      <defs>
        <clipPath id="clip0_7080_102960">
          <rect width="512" height="512" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )),
  GWGoogleLogo: makeCustomIcon((props) => (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <rect width="22" height="22" x="13" y="13" fill="#fff"></rect>
      <polygon
        fill="#1e88e5"
        points="25.68,20.92 26.688,22.36 28.272,21.208 28.272,29.56 30,29.56 30,18.616 28.56,18.616"
      ></polygon>
      <path
        fill="#1e88e5"
        d="M22.943,23.745c0.625-0.574,1.013-1.37,1.013-2.249c0-1.747-1.533-3.168-3.417-3.168 c-1.602,0-2.972,1.009-3.33,2.453l1.657,0.421c0.165-0.664,0.868-1.146,1.673-1.146c0.942,0,1.709,0.646,1.709,1.44 c0,0.794-0.767,1.44-1.709,1.44h-0.997v1.728h0.997c1.081,0,1.993,0.751,1.993,1.64c0,0.904-0.866,1.64-1.931,1.64 c-0.962,0-1.784-0.61-1.914-1.418L17,26.802c0.262,1.636,1.81,2.87,3.6,2.87c2.007,0,3.64-1.511,3.64-3.368 C24.24,25.281,23.736,24.363,22.943,23.745z"
      ></path>
      <polygon fill="#fbc02d" points="34,42 14,42 13,38 14,34 34,34 35,38"></polygon>
      <polygon fill="#4caf50" points="38,35 42,34 42,14 38,13 34,14 34,34"></polygon>
      <path fill="#1e88e5" d="M34,14l1-4l-1-4H9C7.343,6,6,7.343,6,9v25l4,1l4-1V14H34z"></path>
      <polygon fill="#e53935" points="34,34 34,42 42,34"></polygon>
      <path fill="#1565c0" d="M39,6h-5v8h8V9C42,7.343,40.657,6,39,6z"></path>
      <path fill="#1565c0" d="M9,42h5v-8H6v5C6,40.657,7.343,42,9,42z"></path>
    </svg>
  )),
  GWLine: makeCustomIcon((props) => (
    <svg viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <g clipPath="url(#clip0_7080_102964)">
        <ellipse cx="73" cy="440" rx="73" ry="72" fill="currentColor" />
        <circle cx="440" cy="72" r="72" fill="currentColor" />
        <line
          x1="56.172"
          y1="458.458"
          x2="458.458"
          y2="56.1724"
          stroke="currentColor"
          strokeWidth="48"
        />
      </g>
    </svg>
  )),
  GWOutlookLogo: makeCustomIcon((props) => (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        fill="#1976d2"
        d="M28,13h14.533C43.343,13,44,13.657,44,14.467v19.066C44,34.343,43.343,35,42.533,35H28V13z"
      ></path>
      <rect width="14" height="15.542" x="28" y="17.958" fill="#fff"></rect>
      <polygon fill="#1976d2" points="27,44 4,39.5 4,8.5 27,4"></polygon>
      <path
        fill="#fff"
        d="M15.25,16.5c-3.176,0-5.75,3.358-5.75,7.5s2.574,7.5,5.75,7.5S21,28.142,21,24	S18.426,16.5,15.25,16.5z M15,28.5c-1.657,0-3-2.015-3-4.5s1.343-4.5,3-4.5s3,2.015,3,4.5S16.657,28.5,15,28.5z"
      ></path>
      <rect width="2.7" height="2.9" x="28.047" y="29.737" fill="#1976d2"></rect>
      <rect width="2.7" height="2.9" x="31.448" y="29.737" fill="#1976d2"></rect>
      <rect width="2.7" height="2.9" x="34.849" y="29.737" fill="#1976d2"></rect>
      <rect width="2.7" height="2.9" x="28.047" y="26.159" fill="#1976d2"></rect>
      <rect width="2.7" height="2.9" x="31.448" y="26.159" fill="#1976d2"></rect>
      <rect width="2.7" height="2.9" x="34.849" y="26.159" fill="#1976d2"></rect>
      <rect width="2.7" height="2.9" x="38.25" y="26.159" fill="#1976d2"></rect>
      <rect width="2.7" height="2.9" x="28.047" y="22.706" fill="#1976d2"></rect>
      <rect width="2.7" height="2.9" x="31.448" y="22.706" fill="#1976d2"></rect>
      <rect width="2.7" height="2.9" x="34.849" y="22.706" fill="#1976d2"></rect>
      <rect width="2.7" height="2.9" x="38.25" y="22.706" fill="#1976d2"></rect>
      <rect width="2.7" height="2.9" x="31.448" y="19.112" fill="#1976d2"></rect>
      <rect width="2.7" height="2.9" x="34.849" y="19.112" fill="#1976d2"></rect>
      <rect width="2.7" height="2.9" x="38.25" y="19.112" fill="#1976d2"></rect>
    </svg>
  )),
  GWPolyline: makeCustomIcon((props) => (
    <svg viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <g clipPath="url(#clip0_7080_102973)">
        <ellipse cx="73" cy="440" rx="73" ry="72" fill="currentColor" />
        <circle cx="440" cy="72" r="72" fill="currentColor" />
        <circle cx="72" cy="72" r="72" fill="currentColor" />
        <line
          x1="56.172"
          y1="458.458"
          x2="458.458"
          y2="56.1724"
          stroke="currentColor"
          strokeWidth="48"
        />
        <line x1="73" y1="475" x2="73" y2="25" stroke="currentColor" strokeWidth="48" />
      </g>
    </svg>
  )),
}

// The type constraint would otherwise mask the exact keys of CustomIcons that is used to build AllIconsByIconKey, which is why we need this reassignment & casting
const CustomIconsInternal: Record<`GW${string}`, React.FC<CustomIconProps>> = CustomIcons
export default CustomIconsInternal as typeof CustomIcons
